/**
 * useUnifiedCatalog (SYN-1948 WS2, de-org-gated SYN-2517) — is the unified
 * `/catalog` surface on in THIS ENVIRONMENT?
 *
 * The unified catalog consolidates Contracts, Connections, Skills and Agents
 * into a single `/catalog` surface with a Mine | Global Marketplace scope
 * switch, and collapses the Routines nav entry into it.
 *
 * ## What changed (SYN-2517) and what did not
 *
 * It used to be an **org allowlist** baked in at build time
 * (`VITE_UNIFIED_CATALOG_ORG_IDS`), the same defect as `useUnifiedShell`'s one
 * layer up: a per-tenant build argument decides which product a customer sees,
 * and it can never name an org created after the build. Maria, 17 Aug 2026:
 * *"they're the same for anyone, internal or external"*.
 *
 * So org identity is gone. What replaces it is an **environment-level
 * boolean** (`VITE_UNIFIED_CATALOG`), mirroring `lib/community-grab.ts`: one
 * answer for every org in an environment, unset/blank = OFF. Whether this
 * surface is finished enough to be on is a rollout question about the
 * environment; it was never a question about who the customer is.
 *
 * ⚠️ **The flag is deliberately still OFF by default, and that is not a
 * hedge.** `VITE_UNIFIED_CATALOG_ORG_IDS` is not passed by
 * `.github/workflows/deploy-cloch.yml`, so the catalog is off for **everyone**
 * today — internal included. Turning it on would hand every customer a surface
 * nobody currently has, change the member nav from Routines to Mine + Global
 * Marketplace, and make `/routines` redirect to `/catalog`. That is a product
 * decision (SYN-2517's product half), not a fix for an identity gate. What
 * this file guarantees is that the answer is the same for everyone.
 *
 * The shell precondition is kept: the catalog nav entry only exists inside the
 * unified shell, so the shell being on remains a necessary condition rather
 * than an assumption.
 *
 * ⚠️ That precondition is also a TESTING TRAP, and it hid the defect once
 * already. `useUnifiedCatalog` returns false the moment the shell is off, so a
 * test that leaves the real `useUnifiedShell` in place and asserts
 * `toBe(false)` here passes against the OLD org-allowlisted hook without ever
 * reaching the derivation below — measured, not assumed. The guard for this
 * file is therefore `useUnifiedCatalog.journey-parity.test.ts`, which mocks the
 * shell ON so this hook's own answer is the only thing under test.
 */

import { useUnifiedShell } from "./useUnifiedShell";
import { runtimeEnv } from "../lib/runtime-config";

/** Truthy literals, matching lib/community-grab.ts. */
const TRUTHY = new Set(["1", "true", "on", "yes"]);

/**
 * Pure derivation: ON iff the raw value is a recognised truthy literal.
 * Anything else (undefined, "", "0", "false", noise) is OFF — fail closed.
 */
export function deriveUnifiedCatalog(raw: string | null | undefined): boolean {
  if (typeof raw !== "string") {
    return false;
  }
  return TRUTHY.has(raw.trim().toLowerCase());
}

/** Read the environment-level VITE_UNIFIED_CATALOG flag from Vite env. */
export function isUnifiedCatalogEnabled(): boolean {
  return deriveUnifiedCatalog(runtimeEnv("VITE_UNIFIED_CATALOG") as unknown as string);
}

/**
 * Returns true when BOTH:
 *   1. The unified shell is on (the catalog nav entry only exists there), AND
 *   2. `VITE_UNIFIED_CATALOG` is on for this environment.
 *
 * No org id is an input. Two different orgs in one environment always get the
 * same answer.
 */
export function useUnifiedCatalog(): boolean {
  const unifiedShell = useUnifiedShell();
  if (!unifiedShell) {
    return false;
  }
  return isUnifiedCatalogEnabled();
}
